import { Link } from "@inertiajs/react";
import { BookOpen, Calendar, TrendingUp, Upload, Clock, Pencil, Download, Plus } from "lucide-react";
import StudentActivationTile from "@/components/schools/StudentActivationTile";
import MilestonesCard from "@/components/schools/MilestonesCard";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Notice } from "@/components/ui/notice";
import StatusBadge from "@/components/shared/StatusBadge";
import StatTile from "@/components/shared/StatTile";
import ProgressBar from "@/components/shared/ProgressBar";
import { campaignTimeDetail, campaignTimePct } from "@/lib/campaignTimeline";
import { campaignPhase, daysUntil, formatPhaseDate, formatPhaseTime } from "@/lib/campaignPhase";
import { formatDateRange, formatMoney, formatNumber, percentOf } from "@/lib/format";

/**
 * The school admin dashboard heroes: live metrics once setup is complete
 * and donations open, and final results once the campaign finishes.
 * Campaigns still being set up show the setup card (components/schools/setup).
 *
 * Lives outside Schools/Dashboard.jsx so the help centre can render
 * exactly the same hero in a "screenshot" frame.
 *
 * Both heroes are presentational — pass `current_campaign`, the
 * membership shape, and a few path props.
 */

const TITLES = {
  getting_ready: "Getting ready",
  donations_open: "Donations are open",
  reading: "Live now",
  catch_up: "Wrapping up",
  wrapping_up: "Wrapping up",
};

export function RunningCampaignHero({ campaign, membership }) {
  const phase = campaignPhase(campaign);
  const wrappingUp = phase === "catch_up" || phase === "wrapping_up";
  const goalPct = percentOf(campaign.raised_cents, campaign.money_goal_cents);
  const minutePct = percentOf(campaign.minutes_logged, campaign.minute_goal);
  const timePct = campaignTimePct(campaign);

  return (
    <div className="space-y-6">
      {phase === "donations_open" && (
        <Notice icon={Calendar} title={`Reading opens ${formatPhaseDate(campaign.start_at, { timeZone: campaign.timezone })}`}>
          Donations are open. Families can share their pages and start fundraising now.
        </Notice>
      )}
      <WrapUpNotice campaign={campaign} phase={phase} />

      <Card>
        <CardHeader>
          <div className="flex items-center gap-2 mb-1">
            <StatusBadge status={campaign.display_state || campaign.state} />
            <span className="text-xs text-muted-foreground">{formatDateRange(campaign.start_at, campaign.end_at)}</span>
          </div>
          <CardTitle className="text-2xl">
            {TITLES[phase]}
          </CardTitle>
          {campaign.description && (
            <CardDescription className="leading-relaxed">{campaign.description}</CardDescription>
          )}
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <StatTile icon={TrendingUp} label="Raised" value={formatMoney(campaign.raised_cents)} color="primary" />
            <StatTile icon={BookOpen} label="Minutes" value={formatNumber(campaign.minutes_logged)} />
            <StudentActivationTile key={campaign.id} campaign={campaign} canRemind={membership.school_admin} />
            <StatTile
              icon={Calendar}
              label={wrappingUp ? "Days to donate" : "Days left"}
              value={wrappingUp ? daysValue(daysUntil(campaign.grace_ends_at)) : readingDaysValue(campaign.days_remaining)}
            />
          </div>

          <div className="space-y-5">
            <ProgressBar
              label="Time"
              detail={campaignTimeDetail(campaign)}
              pct={timePct}
              footnote={`${timePct}% of campaign timeline`}
              barColor="bg-accent"
              footnoteColor="text-accent"
            />
            {campaign.money_goal_cents > 0 && (
              <ProgressBar
                label="Raised"
                detail={`${formatMoney(campaign.raised_cents)} / ${formatMoney(campaign.money_goal_cents)}`}
                pct={goalPct}
                footnote={`${goalPct}% of fundraising goal`}
                barColor="bg-primary"
                footnoteColor="text-primary"
              />
            )}
            {campaign.minute_goal > 0 && (
              <ProgressBar
                label="Minutes read"
                detail={`${formatNumber(campaign.minutes_logged)} / ${formatNumber(campaign.minute_goal)}`}
                pct={minutePct}
                footnote={`${minutePct}% of reading goal`}
                barColor="bg-info"
                footnoteColor="text-info"
              />
            )}
          </div>

          {campaign.classroom_count === 0 && (
            <Notice
              tone="warning"
              title="No classrooms yet"
              action={membership.school_admin && campaign.import_classrooms_path && (
                <Button size="sm" variant="outline" asChild>
                  <Link href={campaign.import_classrooms_path}>
                    <Upload className="h-4 w-4" />
                    Import classrooms
                  </Link>
                </Button>
              )}
            >
              Add your students so families can activate their pages and donations have somewhere to go.
            </Notice>
          )}

          {membership.school_admin && campaign.edit_path && (
            <div className="pt-2 border-t border-border">
              <Button variant="outline" size="sm" asChild>
                <Link href={campaign.edit_path}>
                  <Pencil className="h-4 w-4" />
                  Edit campaign
                </Link>
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <MilestonesCard
        milestones={campaign.milestones || []}
        manage_path={membership.school_admin ? campaign.milestones_path : null}
      />
    </div>
  );
}

// After the last day of reading: first the overnight catch-up window,
// then donations only until the campaign finishes (BUSINESS_RULES §6).
export function WrapUpNotice({ campaign, phase }) {
  if (phase === "catch_up") {
    return (
      <Notice tone="warning" icon={Clock} title="Reading has ended — last chance to log minutes">
        Minutes can be logged until {formatPhaseTime(campaign.minutes_close_at)}. Donations stay open until {formatPhaseDate(campaign.grace_ends_at)}.
      </Notice>
    );
  }
  if (phase === "wrapping_up") {
    return (
      <Notice tone="info" icon={Clock} title={`Donations close ${formatPhaseDate(campaign.grace_ends_at)}`}>
        Reading ended {formatPhaseDate(campaign.end_at)} and minute logging has closed. Families can still donate until then.
      </Notice>
    );
  }
  return null;
}

export function LockedCampaignHero({ campaign, membership, new_campaign_path }) {
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2 mb-1">
            <StatusBadge status="locked" />
            <span className="text-xs text-muted-foreground">{formatDateRange(campaign.start_at, campaign.end_at)}</span>
          </div>
          <CardTitle className="text-2xl">Final results</CardTitle>
          <CardDescription>This read-a-thon is wrapped up. Final totals below.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-3 gap-3">
            <StatTile icon={TrendingUp} label="Raised" value={formatMoney(campaign.raised_cents)} color="primary" />
            <StatTile icon={BookOpen} label="Minutes" value={formatNumber(campaign.minutes_logged)} />
            <StudentActivationTile key={campaign.id} campaign={campaign} canRemind={membership.school_admin} />
          </div>

          {membership.school_admin && (
            <div className="flex flex-wrap items-center gap-2 pt-4 border-t border-border">
              {new_campaign_path && (
                <Button asChild>
                  <Link href={new_campaign_path}>
                    <Plus className="h-4 w-4" />
                    Start your next read-a-thon
                  </Link>
                </Button>
              )}
              {campaign.congratulations_certificates_path && campaign.student_count > 0 && (
                <Button variant="outline" asChild>
                  <a href={campaign.congratulations_certificates_path}>
                    <Download className="h-4 w-4" />
                    Download certificates
                  </a>
                </Button>
              )}
              {campaign.edit_path && (
                <Button variant="ghost" size="sm" asChild>
                  <Link href={campaign.edit_path}>
                    <Pencil className="h-4 w-4" />
                    Edit campaign
                  </Link>
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <MilestonesCard milestones={campaign.milestones || []} />
    </div>
  );
}

function readingDaysValue(remaining) {
  if (remaining === 0) return "Ended";
  if (remaining === 1) return "Last day";
  return formatNumber(remaining);
}

function daysValue(days) {
  if (days === 0) return "Last day";
  return days === 1 ? "1 day" : `${days} days`;
}
